import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import NewsCard from "@/components/feature/NewsCard";
import { news } from "@/mocks/news";

const PAGE_SIZE = 5;

export default function NewsPage() {
  const categories = useMemo(() => {
    const set = Array.from(new Set(news.map((n) => n.category)));
    return ["全部", ...set];
  }, []);

  const [category, setCategory] = useState("全部");
  const [keyword, setKeyword] = useState("");
  const [count, setCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return news
      .filter((n) => (category === "全部" ? true : n.category === category))
      .filter((n) => (q ? n.title.toLowerCase().includes(q) || n.summary.toLowerCase().includes(q) : true))
      .sort((a, b) => b.publishTime.localeCompare(a.publishTime));
  }, [category, keyword]);

  const visible = filtered.slice(0, count);
  const hasMore = count < filtered.length;

  return (
    <div className="w-full">
      <section className="w-full bg-primary-950 px-4 md:px-6 py-10 md:py-14">
        <nav className="flex items-center gap-1.5 text-xs text-primary-200 mb-3">
          <Link to="/" className="hover:text-accent-400 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-accent-400">资讯</span>
        </nav>
        <h1 className="font-heading font-semibold text-3xl md:text-4xl text-background-50">
          篮球资讯
        </h1>
        <p className="mt-3 max-w-2xl text-primary-100 text-sm md:text-base leading-relaxed">
          汇集赛事新闻、技术分析、球员动态与视频集锦，带你读懂每一场比赛背后的故事与门道。
        </p>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
                <input
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setCount(PAGE_SIZE);
                  }}
                  placeholder="搜索资讯标题或关键词"
                  className="w-full bg-background-100 border border-background-200 rounded-full pl-11 pr-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setCategory(c);
                    setCount(PAGE_SIZE);
                  }}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    category === c
                      ? "bg-secondary-500 text-background-50 dark:text-foreground-950"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="rounded-lg border border-background-200 bg-background-50 py-16 text-center">
            <i className="ri-file-search-line text-4xl text-foreground-400" />
            <p className="mt-3 text-foreground-700">没有找到相关资讯</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词，或切换上方资讯分类试试</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setCategory("全部");
                setCount(PAGE_SIZE);
              }}
              className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {visible.map((item) => (
                <NewsCard key={item.id} item={item} variant="list" />
              ))}
            </div>

            <div className="mt-8 text-center">
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => setCount((c) => c + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-background-300 text-foreground-800 hover:bg-background-100 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" />
                  加载更多资讯
                </button>
              ) : (
                <p className="text-sm text-foreground-500">已经到底啦，共 {filtered.length} 篇资讯</p>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}